import React from 'react';
import { Typography } from '@mui/material';
import {
  resolveVariantClassification,
  geneHasClassificationConflict,
  getBadgeConfig
} from 'src/util/variantPathogenicity';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const TIER_ORDER = [
  'Pathogenic',
  'Likely Pathogenic',
  'Variant of Uncertain Significance'
];

const collectAllVariants = (members) => {
  const out = [];
  members?.forEach((m) => {
    m?.genotypes?.forEach((g) => {
      g?.variants?.forEach((v) => out.push(v));
    });
  });
  return out;
};

export default function VariantPathogenicityLegend({ members }) {
  const t = useAutoTranslation();

  const { usedTiers, hasConflict } = React.useMemo(() => {
    const variants = collectAllVariants(members);
    const tierSet = new Set();
    variants.forEach((v) => {
      const tier = resolveVariantClassification(v);
      if (tier && TIER_ORDER.includes(tier)) tierSet.add(tier);
    });
    return {
      usedTiers: TIER_ORDER.filter((tier) => tierSet.has(tier)),
      hasConflict: geneHasClassificationConflict(variants)
    };
  }, [members]);

  if (usedTiers.length === 0 && !hasConflict) return null;

  return (
    <div style={{ marginBottom: 10 }}>
      <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
        {t('Variant Pathogenicity')}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginTop: 4
        }}
      >
        {usedTiers.map((tier) => {
          const badge = getBadgeConfig(tier);
          return (
            <div
              key={tier}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: badge.color,
                  flexShrink: 0
                }}
              />
              <Typography style={{ fontSize: '0.85rem' }}>
                {t(badge.label)}
              </Typography>
            </div>
          );
        })}
        {hasConflict && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                color: '#F5A623',
                fontSize: '0.95rem',
                lineHeight: 1,
                width: 10,
                textAlign: 'center',
                flexShrink: 0
              }}
            >
              ⚠
            </span>
            <Typography style={{ fontSize: '0.85rem' }}>
              {t('Lab and ClinVar interpretations differ')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
